const publicErrorStatuses = {
  gmail_authentication_required: [401],
  gmail_customer_unavailable: [403, 404],
  gmail_authorization_pending: [409],
  gmail_already_connected: [409],
  gmail_start_conflict: [409],
  gmail_invalid_request: [400],
  gmail_start_unavailable: [503],
} satisfies Record<string, readonly number[]>;
export type GmailHandoffErrorCode = keyof typeof publicErrorStatuses | 'gmail_connection_failed';
export class GmailHandoffError extends Error {
  constructor(readonly code: GmailHandoffErrorCode = 'gmail_connection_failed') {
    super('Could not connect Gmail');
    this.name = 'GmailHandoffError';
  }
}
function isPublicErrorCode(value: string): value is keyof typeof publicErrorStatuses {
  return Object.prototype.hasOwnProperty.call(publicErrorStatuses, value);
}
function publicErrorCode(value: unknown, status: number): GmailHandoffErrorCode {
  if (record(value) && typeof value.error === 'string' && isPublicErrorCode(value.error) && publicErrorStatuses[value.error].includes(status)) return value.error;
  return 'gmail_connection_failed';
}
export function validateGoogleAuthorizationUrl(value: unknown): string {
  try {
    if (typeof value !== 'string' || value.length > 8192) throw new Error();
    const url = new URL(value);
    if (url.origin !== 'https://accounts.google.com' || url.pathname !== '/o/oauth2/v2/auth' || url.username || url.password || url.hash) throw new Error();
    return url.href;
  } catch { throw new Error('Could not connect Gmail'); }
}
export function validCustomerId(value: unknown): value is string { return typeof value === 'string' && /^[a-fA-F0-9]{24}$/.test(value); }
function record(value: unknown): value is Record<string, unknown> { return value !== null && typeof value === 'object' && !Array.isArray(value); }
export function tokenFromState(value: unknown): string | undefined {
  if (!record(value) || !record(value.UserReducer)) return undefined;
  const token = value.UserReducer.accessToken;
  return typeof token === 'string' && /^[^\s]{1,16384}$/.test(token) ? token : undefined;
}
async function boundedJson(response: Response): Promise<unknown> {
  const reader = response.body?.getReader(); if (!reader) throw new Error();
  const chunks: Uint8Array[] = []; let total = 0;
  try {
    for (;;) { const chunk = await reader.read(); if (chunk.done) break; total += chunk.value.byteLength; if (total > 16384) throw new Error(); chunks.push(chunk.value); }
  } finally { await reader.cancel(); }
  const bytes = new Uint8Array(total); let offset = 0; for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
  const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes)); return parsed;
}
export function createGmailHandoff(config: { apiBaseUrl: string; customerId: string; accessToken: string; fetcher?: (url: string, init: RequestInit) => Promise<Response>; timeoutMs?: number }) {
  const api = new URL(config.apiBaseUrl), { customerId, accessToken } = config, timeoutMs = config.timeoutMs ?? 10000;
  if (api.protocol !== 'https:' || api.username || api.password || api.search || api.hash || api.pathname !== '/' || !validCustomerId(customerId) || !/^[^\s]{1,16384}$/.test(accessToken) || !Number.isSafeInteger(timeoutMs) || timeoutMs < 1 || timeoutMs > 30000) throw new Error('Invalid Gmail handoff');
  const fetcher = config.fetcher ?? fetch; let started = false; let active: AbortController | undefined;
  return { cancel: () => active?.abort(), async connect(): Promise<string> {
    if (started) throw new Error('Gmail connection already started'); started = true;
    const controller = new AbortController(); active = controller; const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetcher(`${api.origin}/assistant-customers/${customerId}/gmail/oauth-start`, { method: 'POST', credentials: 'include', redirect: 'error', signal: controller.signal, headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' }, body: '{}' });
      if (!response.ok) throw new GmailHandoffError(publicErrorCode(await boundedJson(response), response.status));
      const value = await boundedJson(response);
      if (controller.signal.aborted || !record(value) || typeof value.expiresAt !== 'string' || !Number.isFinite(Date.parse(value.expiresAt)) || Date.parse(value.expiresAt) <= Date.now()) throw new Error();
      return validateGoogleAuthorizationUrl(value.authorizationUrl);
    } catch (error: unknown) {
      started = false;
      throw error instanceof GmailHandoffError ? error : new GmailHandoffError();
    }
    finally { clearTimeout(timer); controller.abort(); active = undefined; }
  } };
}
